import { ChatPanel } from '@/components/chat/chat-panel'
import { LearningPanel } from '@/components/learning-panel'
import { ResumePreviewPanel } from '@/components/resume-preview-panel'
import { useAuth } from '@/hooks/use-auth'
import { useDocumentProcessingProgress } from '@/hooks/use-document-processing-progress'
import { useDocuments } from '@/hooks/use-documents'
import { subscribe, useEventStream } from '@/hooks/use-event-stream'
import { useMyJobMatches } from '@/hooks/use-external-jobs'
import { useDeleteJobSearch, useJobSearches } from '@/hooks/use-job-searches'
import { useNavVisibility } from '@/hooks/use-nav-visibility'
import { groupMatchesByDocumentId } from '@/lib/group-matches-by-document-id'
import { cn } from '@/lib/utils'
import { documentService } from '@/services/document.service'
import { learningService } from '@/services/learning.service'
import { useChatStore } from '@/stores/chat-store'
import { useUploadProgressStore } from '@/stores/upload-progress-store'
import { type Document, type ExternalJobMatchWithJob, getUserDisplayName } from '@/types'
import {
  Briefcase,
  ChevronDown,
  FileText,
  GraduationCap,
  Loader2,
  LogOut,
  Menu,
  MessageCircle,
  type Sparkles,
  Trash2,
  X,
} from 'lucide-react'
import {
  type CSSProperties,
  type MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Link, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom'

const MATCHED_RESUME_IDS_STORAGE_KEY = 'opportunities.matchedResumeIds'
const VIEWED_READY_MATCHED_RESUME_IDS_STORAGE_KEY = 'opportunities.viewedReadyMatchedResumeIds'

function readMatchedResumeIdsFromStorage(): number[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(MATCHED_RESUME_IDS_STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed
      .map((value) => Number(value))
      .filter((id) => Number.isFinite(id))
      .sort((a, b) => b - a)
  } catch {
    return []
  }
}

function readViewedReadyMatchedResumeIdsFromStorage(): number[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(VIEWED_READY_MATCHED_RESUME_IDS_STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed.map((value) => Number(value)).filter((id) => Number.isFinite(id))
  } catch {
    return []
  }
}

/** Full match count for a resume once job matching has finished (avoids biased partial counts from globally paginated /me matches). */
function sidebarMatchedJobCount(
  resume: Document | undefined,
  matchesForResume: ExternalJobMatchWithJob[] | undefined,
): number {
  // Keep sidebar count aligned with Opportunities list, which is driven by live match rows.
  if (matchesForResume && matchesForResume.length > 0) {
    return matchesForResume.length
  }
  return resume?.job_matching_total ?? 0
}

interface NavItem {
  name: string
  href: string
  icon: typeof Sparkles
  matchPaths: string[]
  badgeKey?: 'resume'
  hidden?: boolean
  locked?: boolean
  lockHint?: string
  children?: NavItem[]
}

export function AppLayout() {
  // Start SSE connection for real-time status updates
  useEventStream()

  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const urlSearch = new URLSearchParams(location.search)
  const resumeIdFromUrl = urlSearch.get('resume_id') ? Number(urlSearch.get('resume_id')) : null
  const jobSearchIdFromUrl = urlSearch.get('job_search_id')
    ? Number(urlSearch.get('job_search_id'))
    : null
  const isOpportunitiesRoute =
    location.pathname === '/opportunities' || location.pathname.startsWith('/opportunities/')
  const isResumeRoute = location.pathname === '/resume' || location.pathname.startsWith('/resume/')
  const [hasMounted, setHasMounted] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [mobileChatOpen, setMobileChatOpen] = useState(false)
  const [logoLoadFailed, setLogoLoadFailed] = useState(false)
  useEffect(() => {
    setHasMounted(true)
  }, [])
  // Close user menu when clicking outside
  useEffect(() => {
    if (!showUserMenu) return
    const handleClickOutside = (e: globalThis.MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest('[data-user-menu]')) {
        setShowUserMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showUserMenu])

  // Close user menu when clicking outside
  useEffect(() => {
    if (!showUserMenu) return
    const handleClickOutside = (e: globalThis.MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest('[data-user-menu]')) {
        setShowUserMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showUserMenu])

  // Close mobile menu on route change
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally trigger on pathname change
  useEffect(() => {
    setMobileMenuOpen(false)
  }, [location.pathname])

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen || mobileChatOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [mobileMenuOpen, mobileChatOpen])

  // Resizable chat panel
  const MIN_CHAT_WIDTH = 300
  const MAX_CHAT_WIDTH = 700
  const DEFAULT_CHAT_WIDTH = 420
  const [chatWidth, setChatWidth] = useState(DEFAULT_CHAT_WIDTH)
  const isResizingRef = useRef(false)

  const handleResizeStart = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      e.preventDefault()
      isResizingRef.current = true
      const startX = e.clientX
      const startWidth = chatWidth

      const handleMouseMove = (moveEvent: globalThis.MouseEvent) => {
        const delta = startX - moveEvent.clientX // dragging left = wider
        const newWidth = Math.min(MAX_CHAT_WIDTH, Math.max(MIN_CHAT_WIDTH, startWidth + delta))
        setChatWidth(newWidth)
      }

      const handleMouseUp = () => {
        isResizingRef.current = false
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }

      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    [chatWidth],
  )
  const { hasResume, resumeCount, documents } = useNavVisibility()
  const { isSuccess: documentsQuerySuccess } = useDocuments()
  const { data: myMatches, isSuccess: matchesQuerySuccess } = useMyJobMatches(0, 100)
  const deleteJobSearch = useDeleteJobSearch()
  const { data: jobSearchesData } = useJobSearches(0, 20)
  const customSearchSets = useMemo(
    () => (jobSearchesData?.items ?? []).filter((search) => search.source_type === 'custom_search'),
    [jobSearchesData],
  )
  const activeCustomSearchResumeIdSet = useMemo(() => {
    const resumeIds = customSearchSets
      .filter((search) => search.status === 'pending' || search.status === 'matching')
      .map((search) => search.document_id)
      .filter((resumeId) => Number.isFinite(resumeId))
    return new Set(resumeIds)
  }, [customSearchSets])
  const matchedJobsByResume = useMemo(
    () => groupMatchesByDocumentId(myMatches?.items),
    [myMatches?.items],
  )
  const matchedResumeIds = useMemo(() => {
    return Object.keys(matchedJobsByResume)
      .map((k) => Number(k))
      .filter((id) => Number.isFinite(id))
      .sort((a, b) => b - a)
  }, [matchedJobsByResume])
  const [persistedMatchedResumeIds, setPersistedMatchedResumeIds] = useState<number[]>(
    readMatchedResumeIdsFromStorage,
  )
  const [viewedReadyMatchedResumeIds, setViewedReadyMatchedResumeIds] = useState<number[]>(
    readViewedReadyMatchedResumeIdsFromStorage,
  )

  // Keep persisted ids in sync with fetched matches and explicit resume_id links.
  useEffect(() => {
    const merged = Array.from(
      new Set([
        ...persistedMatchedResumeIds,
        ...matchedResumeIds,
        ...(resumeIdFromUrl !== null ? [resumeIdFromUrl] : []),
      ]),
    ).sort((a, b) => b - a)

    const unchanged =
      merged.length === persistedMatchedResumeIds.length &&
      merged.every((id, idx) => id === persistedMatchedResumeIds[idx])
    if (unchanged) return

    setPersistedMatchedResumeIds(merged)
    try {
      window.localStorage.setItem(MATCHED_RESUME_IDS_STORAGE_KEY, JSON.stringify(merged))
    } catch {
      // Ignore storage write issues.
    }
  }, [matchedResumeIds, resumeIdFromUrl, persistedMatchedResumeIds])

  // If the user is viewing a specific resume's matched jobs via `resume_id`,
  // ensure that resume appears in the sidebar even if the match grouping
  // hasn't populated the full list yet.
  const effectiveMatchedResumeIds = useMemo(() => {
    return persistedMatchedResumeIds
  }, [persistedMatchedResumeIds])
  const isVisibleResume = useCallback(
    (doc: Document) =>
      doc.document_type === 'resume' &&
      doc.status !== 'failed' &&
      doc.status !== 'invalid_document',
    [],
  )
  const resumeById = useMemo(() => {
    const map: Record<number, Document> = {}
    for (const d of documents ?? []) {
      if (!isVisibleResume(d)) continue
      map[d.id] = d
    }
    return map
  }, [documents, isVisibleResume])
  const readyToBlinkResumeIds = useMemo(() => {
    return effectiveMatchedResumeIds.filter((resumeId) => {
      const resume = resumeById[resumeId]
      const isReady = resume?.job_matching_status === 'completed'
      return isReady && !viewedReadyMatchedResumeIds.includes(resumeId)
    })
  }, [effectiveMatchedResumeIds, resumeById, viewedReadyMatchedResumeIds])
  const generatingMatchResumeIds = useMemo(() => {
    return (documents ?? [])
      .filter(
        (doc) =>
          isVisibleResume(doc) &&
          !activeCustomSearchResumeIdSet.has(doc.id) &&
          (doc.job_matching_status === 'pending' || doc.job_matching_status === 'matching'),
      )
      .map((doc) => doc.id)
      .sort((a, b) => b - a)
  }, [activeCustomSearchResumeIdSet, documents, isVisibleResume])
  const generatingMatchResumeIdSet = useMemo(
    () => new Set(generatingMatchResumeIds),
    [generatingMatchResumeIds],
  )
  const completedResumeIds = useMemo(() => {
    return (documents ?? [])
      .filter((doc) => isVisibleResume(doc) && doc.job_matching_status === 'completed')
      .map((doc) => doc.id)
      .sort((a, b) => b - a)
  }, [documents, isVisibleResume])
  const effectiveOpportunityResumeIds = useMemo(() => {
    return Array.from(new Set([...effectiveMatchedResumeIds, ...completedResumeIds])).sort(
      (a, b) => b - a,
    )
  }, [effectiveMatchedResumeIds, completedResumeIds])
  const readyMatchedResumeIds = useMemo(() => {
    return effectiveOpportunityResumeIds.filter((resumeId) => {
      if (generatingMatchResumeIdSet.has(resumeId)) return false
      const resume = resumeById[resumeId]
      const hasLiveMatches = (matchedJobsByResume[resumeId]?.length ?? 0) > 0
      const isCompleted = resume?.job_matching_status === 'completed'
      return isCompleted || hasLiveMatches
    })
  }, [effectiveOpportunityResumeIds, generatingMatchResumeIdSet, matchedJobsByResume, resumeById])
  const hasOpportunitySubsections =
    generatingMatchResumeIds.length > 0 ||
    readyMatchedResumeIds.length > 0 ||
    customSearchSets.length > 0

  // Drop "viewed" ids only once documents and matches are loaded; otherwise activeReadyIds is
  // empty and would incorrectly clear localStorage (sidebar blink on every login).
  useEffect(() => {
    if (!documentsQuerySuccess || !matchesQuerySuccess) return

    const activeReadyIds = effectiveMatchedResumeIds.filter((resumeId) => {
      if (generatingMatchResumeIdSet.has(resumeId)) return false
      const resume = resumeById[resumeId]
      return resume?.job_matching_status === 'completed'
    })
    const nextViewed = viewedReadyMatchedResumeIds.filter((id) => activeReadyIds.includes(id))
    if (nextViewed.length === viewedReadyMatchedResumeIds.length) return
    setViewedReadyMatchedResumeIds(nextViewed)
    try {
      window.localStorage.setItem(
        VIEWED_READY_MATCHED_RESUME_IDS_STORAGE_KEY,
        JSON.stringify(nextViewed),
      )
    } catch {
      // Ignore storage write issues.
    }
  }, [
    documentsQuerySuccess,
    matchesQuerySuccess,
    effectiveMatchedResumeIds,
    generatingMatchResumeIdSet,
    resumeById,
    viewedReadyMatchedResumeIds,
  ])

  const markMatchedResumeViewed = useCallback((resumeId: number) => {
    setViewedReadyMatchedResumeIds((prev) => {
      if (prev.includes(resumeId)) return prev
      const next = [...prev, resumeId]
      try {
        window.localStorage.setItem(
          VIEWED_READY_MATCHED_RESUME_IDS_STORAGE_KEY,
          JSON.stringify(next),
        )
      } catch {
        // Ignore storage write issues.
      }
      return next
    })
  }, [])

  const handleDeleteJobSearch = useCallback(
    async (searchId: number, searchTitle?: string | null) => {
      const label = searchTitle?.trim() || `Search ${searchId}`
      const confirmed = window.confirm(`Delete "${label}"?`)
      if (!confirmed) return
      try {
        await deleteJobSearch.mutateAsync(searchId)
        if (isOpportunitiesRoute && jobSearchIdFromUrl === searchId) {
          navigate('/opportunities')
        }
      } catch (error) {
        console.error('Failed to delete job search:', error)
      }
    },
    [deleteJobSearch, isOpportunitiesRoute, jobSearchIdFromUrl, navigate],
  )

  useEffect(() => {
    if (resumeIdFromUrl === null) return
    markMatchedResumeViewed(resumeIdFromUrl)
  }, [resumeIdFromUrl, markMatchedResumeViewed])
  const resumeTitleById = useMemo(() => {
    const map: Record<number, string> = {}
    for (const d of documents ?? []) {
      if (d.document_type !== 'resume') continue
      map[d.id] = d.original_filename
    }
    return map
  }, [documents])
  const { processingItems } = useDocumentProcessingProgress(documents)
  const {
    isUploading: isUploadInProgress,
    uploadProgress,
    statusText: uploadStatusText,
  } = useUploadProgressStore()

  const displayName = getUserDisplayName(user)
  const displayInitial = displayName[0]?.toUpperCase() || 'U'

  const coachChildren: NavItem[] = useMemo(
    () => [
      {
        name: 'Resume',
        href: '/resume',
        icon: FileText,
        matchPaths: ['/resume'],
        badgeKey: 'resume' as const,
      },
      {
        name: 'Opportunities',
        href: '/opportunities',
        icon: Briefcase,
        matchPaths: ['/opportunities'],
        hidden: !hasResume,
      },
      {
        name: 'Learn',
        href: '/learn',
        icon: GraduationCap,
        matchPaths: ['/learn'],
        hidden: false,
      },
    ],
    [hasResume],
  )

  // Flat list of all nav items for route guarding
  const allNavItems = coachChildren

  const {
    learningPanelOpen,
    learningContent,
    isLoadingLearning,
    previewPanelOpen,
    resumeContent,
    originalResumeContent,
    selectedTemplate,
    userDocumentId,
    isGeneratingRevision,
    isUpdatingPreview,
    previewJobTitle,
    previewCompany,
    setLearningPanelOpen,
    setLearningContent,
    setIsLoadingLearning,
    setPreviewPanelOpen,
    setSelectedTemplate,
    setIsGeneratingRevision,
  } = useChatStore()

  // Redirect away from hidden/locked routes
  const isOnLockedRoute = allNavItems.some(
    (item) =>
      (item.hidden || item.locked) &&
      item.matchPaths.some(
        (path) => location.pathname === path || location.pathname.startsWith(`${path}/`),
      ),
  )

  // Navigate to resume page when preview opens from another page
  useEffect(() => {
    if (previewPanelOpen && !isResumeRoute && userDocumentId !== null) {
      navigate('/resume')
    }
  }, [previewPanelOpen, isResumeRoute, userDocumentId, navigate])

  // Side panel logic: learning takes priority
  const showLearningPanel = learningPanelOpen
  const showPreviewPanel =
    isResumeRoute && userDocumentId !== null && previewPanelOpen && !showLearningPanel

  // Handle generate PDF from preview panel
  const handleGenerateFromPreview = async () => {
    if (!userDocumentId) return

    setIsGeneratingRevision(true)
    try {
      const revision = await documentService.generateRevision(userDocumentId, {
        template_id: selectedTemplate,
        use_draft: true,
        trigger: 'button',
      })

      // If already ready, download immediately
      let finalStatus: string = revision.status
      if (finalStatus === 'compiling') {
        // Wait for SSE event instead of polling
        finalStatus = await new Promise<string>((resolve) => {
          const timeout = setTimeout(() => resolve('timeout'), 60000)
          const unsubscribe = subscribe((event) => {
            if (event.type !== 'revision_status_changed') return
            const data = event.data as { revision_id: number; status: string }
            if (data.revision_id === revision.id) {
              clearTimeout(timeout)
              unsubscribe()
              resolve(data.status)
            }
          })
        })
      }

      if (finalStatus === 'ready') {
        const result = await documentService.getRevisionDownloadUrl(
          userDocumentId,
          revision.id,
          'pdf',
        )
        // Prefer `window.open` so the browser treats this as a navigation/download flow
        // even when the trigger happens after async work.
        const openedWindow = window.open(result.download_url, '_blank')

        if (!openedWindow) {
          // Fallback to anchor click
          const link = document.createElement('a')
          link.href = result.download_url
          link.download = result.filename
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        }
      } else if (finalStatus === 'failed') {
        alert('Failed to generate resume. Please try again.')
      } else if (finalStatus === 'timeout') {
        alert('Resume generation is taking longer than expected. Please try again.')
      }
    } catch (error) {
      console.error('Failed to generate revision from preview:', error)
      alert('Failed to generate resume. Please try again.')
    } finally {
      setIsGeneratingRevision(false)
    }
  }

  // Mark learning module as complete
  async function handleMarkModuleComplete() {
    if (!learningContent?.module_id) return
    try {
      await learningService.updateProgress(learningContent.module_id, {
        status: 'completed',
        progress_percent: 100,
      })
      setLearningContent((prev) =>
        prev ? { ...prev, progress: { status: 'completed', progress_percent: 100 } } : prev,
      )
    } catch (error) {
      console.error('Failed to mark module complete:', error)
    }
  }

  // Determine center content
  const renderCenterContent = () => {
    if (showLearningPanel) {
      return (
        <LearningPanel
          content={learningContent}
          isLoading={isLoadingLearning}
          onClose={() => {
            setLearningPanelOpen(false)
            setIsLoadingLearning(false)
          }}
          onMarkComplete={handleMarkModuleComplete}
        />
      )
    }
    if (showPreviewPanel) {
      return (
        <ResumePreviewPanel
          content={resumeContent}
          originalContent={originalResumeContent}
          template={selectedTemplate}
          onTemplateChange={setSelectedTemplate}
          onGenerate={handleGenerateFromPreview}
          onClose={() => {
            setPreviewPanelOpen(false)
          }}
          isGenerating={isGeneratingRevision}
          isUpdatingContent={isUpdatingPreview}
          jobTitle={previewJobTitle}
          company={previewCompany}
        />
      )
    }
    return null
  }

  const sidePanelContent = renderCenterContent()

  const pelgoLogoLink = (opts?: { compact?: boolean; onAfterNavigate?: () => void }) => (
    <Link to="/resume" className="flex items-center" onClick={() => opts?.onAfterNavigate?.()}>
      <div
        className={cn(
          'overflow-hidden bg-transparent shrink-0',
          opts?.compact ? 'h-[22px] w-auto max-w-[98px]' : 'h-[25px] w-auto max-w-[126px]',
        )}
      >
        {!logoLoadFailed ? (
          <img
            src="/logomark-white.png"
            alt="Pelgo"
            className="h-full w-full object-contain object-left"
            onError={() => setLogoLoadFailed(true)}
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-white font-bold text-sm">
            P
          </div>
        )}
      </div>
    </Link>
  )

  const notifySidebarNavigation = useCallback(() => {
    if (typeof window === 'undefined') return
    window.dispatchEvent(new CustomEvent('pelgo:sidebar-navigation'))
  }, [])

  const sidebarNav = (
    <nav className="flex flex-col gap-0.5 px-3 flex-1 pt-2 md:pt-3">
      <div className="px-3 py-1 text-xs font-medium tracking-wide text-muted-foreground/80">
        Navigation
      </div>

      <div className="flex flex-col gap-0.5 pl-1 md:pl-2 flex-1 min-h-0">
        {coachChildren.map((item) => {
          const Icon = item.icon
          const isActive = item.matchPaths.some(
            (path) => location.pathname === path || location.pathname.startsWith(`${path}/`),
          )
          const badge = item.badgeKey === 'resume' && resumeCount > 0 ? resumeCount : null
          let resumeProgress: { progress: number; label: string } | null = null
          if (item.badgeKey === 'resume') {
            if (processingItems.length > 0) {
              resumeProgress = {
                progress: processingItems[0]?.progress ?? 0,
                label: processingItems[0]?.statusText || 'Processing...',
              }
            } else if (isUploadInProgress) {
              resumeProgress = {
                progress: uploadProgress,
                label: uploadStatusText || 'Uploading...',
              }
            }
          }

          if (item.hidden) return null

          return (
            <div key={item.name}>
              <Link
                to={item.href}
                onClick={notifySidebarNavigation}
                className={cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-blue-100 text-blue-900 dark:bg-blue-950/50 dark:text-blue-100'
                    : 'text-muted-foreground hover:bg-muted/80 hover:text-foreground',
                )}
              >
                <Icon className={cn('h-4 w-4', isActive && 'text-blue-600')} />
                <span className="flex-1">{item.name}</span>
                {badge !== null && (
                  <span
                    className={cn(
                      'text-xs px-1.5 py-0.5 rounded-full min-w-[20px] text-center font-medium',
                      isActive
                        ? 'bg-blue-200/80 text-blue-900 dark:bg-blue-900/60 dark:text-blue-100'
                        : 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300',
                    )}
                  >
                    {badge}
                  </span>
                )}
              </Link>

              {item.href === '/opportunities' && hasOpportunitySubsections && (
                <div className="mt-1 pl-6 space-y-1">
                  <Link
                    to="/opportunities"
                    onClick={notifySidebarNavigation}
                    className={cn(
                      'block px-2 py-1 text-[13px] rounded-md transition-colors',
                      isOpportunitiesRoute &&
                        resumeIdFromUrl === null &&
                        jobSearchIdFromUrl === null
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                    )}
                  >
                    Launch job search agent
                  </Link>
                  {customSearchSets.length > 0 && (
                    <div className="pt-1">
                      <div className="px-2 pb-1 text-[11px] uppercase tracking-wide text-muted-foreground/80">
                        Job Search Agents
                      </div>
                      {(() => {
                        const processingSearches = customSearchSets
                          .filter(
                            (search) => search.status === 'pending' || search.status === 'matching',
                          )
                          .slice(0, 8)
                        const readySearches = customSearchSets
                          .filter(
                            (search) => search.status !== 'pending' && search.status !== 'matching',
                          )
                          .slice(0, 8)
                        return (
                          <>
                            {processingSearches.length > 0 && (
                              <div className="space-y-2">
                                {processingSearches.map((search) => {
                                  const isActiveGenerated =
                                    isOpportunitiesRoute && jobSearchIdFromUrl === search.id
                                  const total = search.total_jobs ?? null
                                  const completed = search.completed_jobs ?? null
                                  const computedProgress =
                                    total && total > 0 && completed !== null
                                      ? Math.max(
                                          0,
                                          Math.min(100, Math.round((completed / total) * 100)),
                                        )
                                      : null
                                  const progress = computedProgress ?? 0
                                  return (
                                    <div key={search.id} className="relative">
                                      <Link
                                        to={`/opportunities?job_search_id=${search.id}`}
                                        onClick={notifySidebarNavigation}
                                        className={cn(
                                          'block rounded-md px-2 py-1 pr-8 transition-colors',
                                          isActiveGenerated
                                            ? 'bg-blue-50 text-blue-700'
                                            : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                                        )}
                                        title="Matched jobs are still being generated"
                                      >
                                        <div className="flex items-center gap-2">
                                          <Loader2 className="h-5 w-5 shrink-0 animate-spin text-blue-500" />
                                          <div className="min-w-0 flex-1">
                                            <div className="truncate text-[12px] font-medium text-foreground">
                                              {search.job_title || `Search ${search.id}`}
                                            </div>
                                            <div className="text-[11px] text-muted-foreground">
                                              Working...
                                            </div>
                                          </div>
                                          <div className="text-[12px] font-semibold tabular-nums text-foreground">
                                            {computedProgress !== null ? `${progress}%` : '...'}
                                          </div>
                                        </div>
                                      </Link>
                                      <button
                                        type="button"
                                        onClick={(event) => {
                                          event.preventDefault()
                                          event.stopPropagation()
                                          void handleDeleteJobSearch(search.id, search.job_title)
                                        }}
                                        className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                                        aria-label={`Delete ${search.job_title || `Search ${search.id}`}`}
                                        title="Delete search"
                                      >
                                        <Trash2 className="h-3.5 w-3.5" />
                                      </button>
                                    </div>
                                  )
                                })}
                              </div>
                            )}
                            {readySearches.length > 0 && (
                              <div className="space-y-1">
                                {readySearches.map((search) => {
                                  const isActiveGenerated =
                                    isOpportunitiesRoute && jobSearchIdFromUrl === search.id
                                  const statusLabel =
                                    search.match_count != null ? `${search.match_count}` : 'Done'
                                  return (
                                    <div key={search.id} className="relative">
                                      <Link
                                        to={`/opportunities?job_search_id=${search.id}`}
                                        onClick={notifySidebarNavigation}
                                        className={cn(
                                          'block rounded-md transition-colors',
                                          isActiveGenerated
                                            ? 'bg-blue-50 text-blue-700'
                                            : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                                        )}
                                      >
                                        <div className="flex items-center justify-between gap-2 px-2 py-1 pr-8">
                                          <span className="truncate text-[12px] font-medium">
                                            {search.job_title || `Search ${search.id}`}
                                          </span>
                                          <span className="text-[10px] text-muted-foreground tabular-nums">
                                            {statusLabel}
                                          </span>
                                        </div>
                                      </Link>
                                      <button
                                        type="button"
                                        onClick={(event) => {
                                          event.preventDefault()
                                          event.stopPropagation()
                                          void handleDeleteJobSearch(search.id, search.job_title)
                                        }}
                                        className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                                        aria-label={`Delete ${search.job_title || `Search ${search.id}`}`}
                                        title="Delete search"
                                      >
                                        <Trash2 className="h-3.5 w-3.5" />
                                      </button>
                                    </div>
                                  )
                                })}
                              </div>
                            )}
                          </>
                        )
                      })()}
                    </div>
                  )}
                  {(generatingMatchResumeIds.length > 0 || readyMatchedResumeIds.length > 0) && (
                    <div className="pt-1">
                      <div className="px-2 pb-1 text-[11px] uppercase tracking-wide text-muted-foreground/80">
                        Resume Scan Agents
                      </div>
                      {generatingMatchResumeIds.length > 0 && (
                        <div className="space-y-2">
                          {generatingMatchResumeIds.map((resumeId) => {
                            const resume = resumeById[resumeId]
                            const total = resume?.job_matching_total ?? null
                            const completed = resume?.job_matching_completed_count ?? null
                            const computedProgress =
                              total && total > 0 && completed !== null
                                ? Math.max(0, Math.min(100, Math.round((completed / total) * 100)))
                                : null
                            const progress = computedProgress ?? 0
                            return (
                              <Link
                                key={resumeId}
                                to={`/opportunities?resume_id=${resumeId}`}
                                onClick={() => {
                                  notifySidebarNavigation()
                                  markMatchedResumeViewed(resumeId)
                                }}
                                className={cn(
                                  'block rounded-md px-2 py-1 transition-colors',
                                  isOpportunitiesRoute && resumeIdFromUrl === resumeId
                                    ? 'bg-blue-50 text-blue-700'
                                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                                )}
                                title="Matched jobs are still being generated"
                              >
                                <div className="flex items-center gap-2">
                                  <Loader2 className="h-5 w-5 shrink-0 animate-spin text-blue-500" />
                                  <div className="min-w-0 flex-1">
                                    <div className="truncate text-[12px] font-normal text-foreground">
                                      {resumeTitleById[resumeId] || `Resume ${resumeId}`}
                                    </div>
                                    <div className="text-[11px] text-muted-foreground">
                                      Working...
                                    </div>
                                  </div>
                                  <div className="text-[12px] font-semibold tabular-nums text-foreground">
                                    {computedProgress !== null ? `${progress}%` : '...'}
                                  </div>
                                </div>
                              </Link>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )}
                  {readyMatchedResumeIds.map((resumeId) =>
                    (() => {
                      const shouldBlink = readyToBlinkResumeIds.includes(resumeId)
                      const isViewed = viewedReadyMatchedResumeIds.includes(resumeId)
                      const resumeDoc = resumeById[resumeId]
                      const matchesForResume = matchedJobsByResume[resumeId]
                      const sidebarCount = sidebarMatchedJobCount(resumeDoc, matchesForResume)

                      const content = isViewed ? (
                        <div className="flex items-center justify-between gap-2 px-2 py-1">
                          <span className="truncate text-[12px] font-normal">
                            {resumeTitleById[resumeId] || `Resume ${resumeId}`}
                          </span>
                          <span className="text-[10px] text-muted-foreground tabular-nums">
                            {sidebarCount}
                          </span>
                        </div>
                      ) : (
                        <div
                          className={cn(
                            'flex items-center gap-2 px-2 py-1',
                            shouldBlink && 'animate-pulse',
                          )}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-[12px] font-normal text-blue-700">
                              {resumeTitleById[resumeId] || `Resume ${resumeId}`}
                            </div>
                            <div className="text-[11px] text-muted-foreground">Done</div>
                          </div>
                          <span className="text-[12px] font-semibold tabular-nums text-foreground">
                            100%
                          </span>
                        </div>
                      )

                      return (
                        <Link
                          key={resumeId}
                          to={`/opportunities?resume_id=${resumeId}`}
                          onClick={() => {
                            notifySidebarNavigation()
                            markMatchedResumeViewed(resumeId)
                          }}
                          className={cn(
                            'block rounded-md transition-colors',
                            resumeIdFromUrl === resumeId
                              ? 'bg-blue-50 text-blue-700'
                              : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                          )}
                        >
                          {content}
                        </Link>
                      )
                    })(),
                  )}
                </div>
              )}

              {resumeProgress && (
                <div className="px-3 mt-1 mb-1">
                  <p className="text-[10px] text-muted-foreground mb-1 truncate">
                    {resumeProgress.label}
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 rounded-full transition-all duration-500"
                        style={{ width: `${resumeProgress.progress}%` }}
                      />
                    </div>
                    <span className="text-[10px] text-muted-foreground tabular-nums">
                      {Math.round(resumeProgress.progress)}%
                    </span>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </nav>
  )

  const legalLinks = (
    <div className="px-4 py-3 border-t">
      <div className="flex items-center justify-center text-[10px] text-gray-500">
        <a
          href="https://pelgo.com/terms-of-service"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-gray-700 transition-colors"
        >
          Terms of Use
        </a>
        <span className="mx-2 select-none text-gray-400" aria-hidden="true">
          •
        </span>
        <a
          href="https://pelgo.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-gray-700 transition-colors"
        >
          Privacy Policy
        </a>
      </div>
    </div>
  )

  const userMenuDropdown = (placement: 'sidebar' | 'header') => (
    <div data-user-menu className="relative">
      <button
        type="button"
        onClick={() => setShowUserMenu((prev) => !prev)}
        className={cn(
          'flex items-center gap-2.5 rounded-lg transition-colors',
          placement === 'sidebar'
            ? 'w-full px-2 py-1.5 hover:bg-muted'
            : 'px-2 py-1.5 hover:bg-white/10 border border-transparent hover:border-white/20',
        )}
      >
        <div
          className={cn(
            'w-8 h-8 rounded-full border flex items-center justify-center text-xs font-medium shrink-0',
            placement === 'sidebar'
              ? 'bg-muted border-border text-muted-foreground'
              : 'bg-white border-white text-[#9CA3AF]',
          )}
        >
          {displayInitial}
        </div>
        {placement === 'header' && (
          <span className="text-sm font-medium text-white max-w-[140px] truncate">
            {displayName}
          </span>
        )}
        {placement === 'sidebar' && (
          <div className="flex-1 min-w-0 text-left">
            <p className="text-sm font-medium truncate leading-tight">{displayName}</p>
            {user?.email && displayName !== user.email?.split('@')[0] && (
              <p className="text-[11px] text-muted-foreground truncate leading-tight">
                {user.email}
              </p>
            )}
          </div>
        )}
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 shrink-0 transition-transform',
            placement === 'sidebar' ? 'text-muted-foreground' : 'text-white/80',
            showUserMenu && 'rotate-180',
          )}
        />
      </button>
      {showUserMenu && (
        <div
          className={cn(
            'absolute rounded-lg border bg-background shadow-lg py-1 z-50',
            placement === 'sidebar'
              ? 'bottom-full left-3 right-3 mb-1'
              : 'top-full right-0 mt-1 min-w-[180px]',
          )}
        >
          <button
            type="button"
            onClick={async () => {
              setShowUserMenu(false)
              try {
                await logout()
                navigate('/login', { replace: true })
              } catch (error) {
                console.error('Logout failed:', error)
              }
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-muted transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Log out
          </button>
        </div>
      )}
    </div>
  )

  const sidebarContentMobile = (
    <>
      <div className="px-5 py-4 flex items-center justify-between border-b border-border/60">
        {pelgoLogoLink({ onAfterNavigate: () => setMobileMenuOpen(false) })}
        <button
          type="button"
          className="p-1 rounded-lg hover:bg-muted"
          onClick={() => setMobileMenuOpen(false)}
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      {sidebarNav}
      {legalLinks}
      <div className="border-t px-3 py-2 relative mt-auto">{userMenuDropdown('sidebar')}</div>
    </>
  )

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-[#102A56]">
      {/* Desktop top bar — logo + profile only (no search, no notifications) */}
      <header className="hidden md:flex h-14 shrink-0 items-center justify-between gap-4 bg-[#102A56] px-5">
        {pelgoLogoLink()}
        <div className="flex-1 min-w-0" aria-hidden />
        <div className="relative shrink-0">{userMenuDropdown('header')}</div>
      </header>

      <div
        className="flex flex-1 min-h-0 min-w-0 md:gap-2 md:px-2 md:pb-2 bg-[#102A56]"
        style={
          {
            '--coach-panel-width': `${chatWidth}px`,
          } as CSSProperties
        }
      >
        {/* Mobile top bar */}
        <div className="md:hidden fixed top-0 left-0 right-0 z-30 h-12 border-b bg-background flex items-center justify-between px-3">
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className="p-1.5 rounded-lg hover:bg-muted"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="absolute left-1/2 -translate-x-1/2">
            {pelgoLogoLink({ compact: true })}
          </div>
          <button
            type="button"
            onClick={() => setMobileChatOpen(true)}
            className="p-1.5 rounded-lg hover:bg-muted shrink-0"
            aria-label="Open coach"
          >
            <MessageCircle className="h-5 w-5" />
          </button>
        </div>

        {/* Mobile sidebar overlay */}
        {mobileMenuOpen && (
          <div
            className="md:hidden fixed inset-0 z-40 bg-black/50"
            onClick={() => setMobileMenuOpen(false)}
            onKeyDown={(e) => e.key === 'Escape' && setMobileMenuOpen(false)}
          />
        )}

        {/* Mobile sidebar drawer */}
        <aside
          className={cn(
            'md:hidden fixed inset-y-0 left-0 z-50 w-64 bg-background flex flex-col transition-transform duration-200',
            mobileMenuOpen ? 'translate-x-0' : '-translate-x-full',
          )}
          onClickCapture={notifySidebarNavigation}
        >
          <div className="flex flex-col flex-1 min-h-0">{sidebarContentMobile}</div>
        </aside>

        {/* Mobile chat overlay */}
        {mobileChatOpen && (
          <div className="md:hidden fixed inset-0 z-40 bg-background flex flex-col">
            <div className="h-12 border-b flex items-center justify-between px-4 shrink-0">
              <span className="text-sm font-semibold">Pelgo coach</span>
              <button
                type="button"
                onClick={() => setMobileChatOpen(false)}
                className="p-1.5 rounded-lg hover:bg-muted"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 min-h-0">
              <ChatPanel />
            </div>
          </div>
        )}

        {/* Desktop combined block — sidebar + main content in one component */}
        <div
          className={cn(
            'hidden md:flex flex-1 min-w-0 min-h-0 rounded-lg border border-slate-200 bg-white overflow-hidden',
            'transition-all duration-1000 ease-out',
            hasMounted ? 'translate-x-0 opacity-100' : '-translate-x-6 opacity-0',
          )}
        >
          <aside
            className="w-56 border-r bg-background flex-col shrink-0 min-h-0 flex"
            onClickCapture={notifySidebarNavigation}
          >
            {sidebarNav}
            {legalLinks}
          </aside>

          {/* Center content */}
          <main className="flex-1 overflow-hidden relative bg-[#F3F4F6]">
            {isOnLockedRoute ? (
              <Navigate to="/resume" replace />
            ) : (
              <>
                {/* Regular page content - use hidden instead of unmount to preserve state */}
                <div
                  key={location.pathname}
                  className={cn('h-full animate-page-in', sidePanelContent ? 'hidden' : '')}
                >
                  <Outlet />
                </div>
                {/* Side panel content overlays center */}
                {sidePanelContent && <div className="h-full">{sidePanelContent}</div>}
              </>
            )}
          </main>
        </div>

        {/* Mobile main content */}
        <main className="flex-1 overflow-hidden relative pt-12 md:hidden">
          {isOnLockedRoute ? (
            <Navigate to="/resume" replace />
          ) : (
            <>
              <div className={cn('h-full', sidePanelContent ? 'hidden' : '')}>
                <Outlet />
              </div>
              {sidePanelContent && <div className="h-full">{sidePanelContent}</div>}
            </>
          )}
        </main>

        {/* Right chat panel with resize handle (desktop only) */}
        <div
          className={cn(
            'hidden md:flex shrink-0 relative bg-background border border-slate-200 rounded-lg',
            'transition-all duration-1000 ease-out delay-150',
            hasMounted ? 'translate-x-0 opacity-100' : 'translate-x-6 opacity-0',
          )}
          style={{ width: chatWidth }}
        >
          <div
            className="w-1 cursor-col-resize hover:bg-blue-400/50 active:bg-blue-400/70 transition-colors shrink-0"
            onMouseDown={handleResizeStart}
            title="Drag to resize"
          />
          <div className="flex-1 min-w-0 min-h-0 flex flex-col">
            <ChatPanel />
          </div>
        </div>
      </div>
    </div>
  )
}
